// src/utils/auth.js
import { Amplify } from "aws-amplify";
import { signIn, signOut, signUp, confirmSignUp, resendSignUpCode,
         confirmSignIn, fetchAuthSession, getCurrentUser,
         forgotPassword, confirmForgotPassword } from "@aws-amplify/auth";
import { AmplifyEventBus } from "aws-amplify-vue";
import cookieService from "../services/cookie-service";
import awsconfig from "../aws-exports";
import router from "@/router"; 

/**
 * Get the currently authenticated user.
 */
async function getUser() {
  console.log("getUser called");
  try {
    const user = await getCurrentUser();
    return user || null;
  } catch (err) {
    console.log("getUser error:", err);
    return null;
  }
}

/**
 * Sign up a new user.
 */
async function signUpUser(username, password, email, isOpten) {
  try {
    const { userId, nextStep } = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          name: username,
          website: isOpten ? "opten" : "",
        },
      },
    });

    AmplifyEventBus.$emit("localUser", userId);
    if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
      // Show confirmation form
      
    } else {
      AmplifyEventBus.$emit("authState", "signIn");
    }

    return { code: "success", userId };
  } catch (err) {
    console.error("Sign up error:", err);
    return err;
  }
}

/**
 * Confirm a new sign-up using the code sent via email.
 */
async function confirmSignUpUser(username, code) {
  try {
    await confirmSignUp({ username, confirmationCode: code });
    AmplifyEventBus.$emit("authState", "signIn");
    return { code: "success" };
  } catch (err) {
    console.error("Confirm sign-up error:", err);
    throw err;
  }
}

/**
 * Resend the sign-up code.
 */
async function resendSignUp(username) {
  try {
    await resendSignUpCode({ username });
    return { code: "success" };
  } catch (err) {
    console.error("Resend sign-up error:", err);
    return err;
  }
}

/**
 * Forgot password workflow — step 1: request reset code.
 */
async function forgotPasswordRequest(username) {
  try {
    await forgotPassword({ username });
    return { code: "success" };
  } catch (err) {
    console.error("Forgot password error:", err);
    return err;
  }
}

/**
 * Forgot password workflow — step 2: confirm new password with code.
 */
async function confirmNewPassword(username, code, newPassword) {
  try {
    await confirmForgotPassword({ username, confirmationCode: code, newPassword });
    AmplifyEventBus.$emit("authState", "signIn");
    return { code: "success" };
  } catch (err) {
    console.error("Confirm new password error:", err);
    return err;
  }
}

/**
 * Sign in (handles EMAIL_OTP challenge automatically).
 */
async function signInUser(username, password) {
  console.log("signIn called");
  try {
    const { nextStep, isSignedIn } = await signIn({ username, password });

    // 🔸 If MFA required
    if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
      console.warn("❌ OTP required");
      return { code: "MFA_REQUIRED" };
    }

    // 🔹 If signed in directly
    if (isSignedIn) {
      cookieService.eraseCookie("source");
      AmplifyEventBus.$emit("authState", "signedIn");
      return { code: "success" };
    }

    console.warn("Unknown sign-in result:", nextStep);
    return { code: "unknown" };
  } catch (err) {
    console.error("Sign-in error:", err);
    switch (err.name) {
      case "UserNotConfirmedException":
        AmplifyEventBus.$emit("authState", "confirmSignUp");
        break;
      case "PasswordResetRequiredException":
        AmplifyEventBus.$emit("authState", "forgotPassword");
        break;
      case "NotAuthorizedException":
        console.warn("❌ Invalid credentials");
        break;
      case "UserNotFoundException":
        console.warn("❌ User not found");
        break;
      default:
        console.error(err);
    }
    return err;
  }
}

/**
 * Confirm MFA / OTP step.
 */
async function handleMfaSubmit(code) {
  try {
    const pending = localStorage.getItem("pendingUser");
    if (!pending) throw new Error("Missing pending user context");
    const { username } = JSON.parse(pending);

    const { isSignedIn } = await confirmSignIn({ challengeResponse: code });
    if (!isSignedIn) throw new Error("Failed to confirm sign-in");

    await fetchAuthSession();
    localStorage.removeItem("pendingUser");
    AmplifyEventBus.$emit("authState", "signedIn");
    return { code: "success" };
  } catch (error) {
    console.error("❌ MFA failed:", error);
    return error;
  }
}

/**
 * Global sign-out.
 */
async function signOutUser() {
  try {
    await signOut({ global: true });
    AmplifyEventBus.$emit("authState", "signedOut");
    return { code: "success" };
  } catch (err) {
    console.error("Sign-out error:", err);
    return err;
  }
}

async function checkAuthSession(store) {
  console.log("[auth.js] checkAuthSession called");
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();

    // ✅ Detect federated sign-ins
    const isFederated =
      !!session?.tokens?.idToken?.payload?.identities &&
      Array.isArray(session.tokens.idToken.payload.identities);

    if (user) {
      store.commit(
        "general/userState",
        "signedIn"
      );
      console.log(`[auth.js] User detected (${isFederated ? "federated" : "signedIn"})`);
      return user;
    }

    store.commit("general/userState", "signedOut");
    return null;
  } catch (err) {
    console.warn("[auth.js] No active session", err);
    store.commit("general/userState", "signedOut");
    return null;
  }
}

export {
  getUser,
  checkAuthSession,
  signUpUser as signUp,
  confirmSignUpUser as confirmSignUp,
  resendSignUp,
  signInUser as signIn,
  signOutUser as signOut,
  forgotPasswordRequest as forgotPassword,
  confirmNewPassword as confirmNewpass,
  handleMfaSubmit,
};
